// src/pages/users/UserForm.tsx
import React from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { useEntityForm } from '../../hooks/useEntityForm';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import { EntityFormLayout } from '../../components/form/EntityFormLayout';
import { DynamicFormFields } from '../../components/form/DynamicFormFields';
import {
  userFormFields,
  userFormSchema,
  userFormInitialValues,
  userConfig, 
} from './user.config';
import {
  CreateUserRequest,
  UpdateUserRequest,
  User,
} from '../../types/users.types'; 
import { useUserFormOptions } from '../../hooks/useFormOptions';

const UserForm: React.FC = () => {
  const {
    id,
    mode,
    data,
    loading,
    handleSubmit,
    handleReset,
    navigate,
  } = useEntityForm<User, CreateUserRequest, UpdateUserRequest>({
    entityName: userConfig.entityName,
    getEntity: userConfig.api.getOne,
    createEntity: userConfig.api.create,
    updateEntity: userConfig.api.update,
    redirectPath: userConfig.redirectPath,
    formFields: userFormFields,
  });

  const formOptions:any = useUserFormOptions();

  const dynamicFields = userFormFields.map(field =>
    formOptions[field.name]
      ? { ...field, options: formOptions[field.name] }
      : field
  );

  const deleteHandler = useDeleteHandler();
  const handleDelete = () =>
    id &&
    deleteHandler({
      id,
      entityName: userConfig.entityName,
      deleteApi: userConfig.api.delete,
      redirectPath: userConfig.redirectPath,
    });

  const handleSubmitWithOptionalPassword = async (values: any) => {
    const payload = { ...values };

    // Remove password if it's empty
    if (!payload.password) {
      delete payload.password;
    }
    if (!payload.mustResetPassword) {
      delete payload.mustResetPassword;
    }
    if (!payload.sendTempPassword) {
      delete payload.sendTempPassword;
    }

    await handleSubmit(payload);
  };

  return (
    <div className="h-full flex flex-col">
      <Formik
        key={`${id}-${mode}`}
        enableReinitialize
        initialValues={userFormInitialValues(data)}
        validationSchema={userFormSchema}
        onSubmit={handleSubmitWithOptionalPassword}
        context={{ mode }}
        autoComplete="off"
      >
        {({ dirty }) => (
          <FormikForm className="flex flex-col flex-grow h-full">
            <EntityFormLayout
              title={
                mode === 'create'
                  ? `Create ${userConfig.entityName}`
                  : mode === 'edit'
                  ? `Edit ${userConfig.entityName}`
                  : `View ${userConfig.entityName}`
              }
              mode={mode}
              data={data}
              loading={loading}
              dirty={dirty}
              onGoBack={() => navigate(userConfig.redirectPath)}
              onReset={handleReset}
              onDelete={handleDelete}
              onCreateNew={() => navigate(`${userConfig.redirectPath}/create`)}
              onEdit={() => navigate(`${userConfig.redirectPath}/edit/${id}`)}
              onView={() => navigate(`${userConfig.redirectPath}/view/${id}`)}
            >
              <DynamicFormFields fields={dynamicFields} mode={mode} />
            </EntityFormLayout>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default UserForm;
